import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { formatINR, resolveImage } from "../../../shared/services/api";
import { ChevronLeft, ChevronRight, Home, MapPin } from "lucide-react";

/**
 * v2.70 Phase 4 — SimilarProperties carousel
 * Fetches /api/properties/{id}/similar on mount; renders horizontal swipeable
 * card row. Falls back gracefully on empty/error.
 */
export default function SimilarProperties({ propertyId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const trackRef = useRef(null);

  useEffect(() => {
    if (!propertyId) return;
    setLoading(true);
    api.get(`/properties/${propertyId}/similar`)
      .then((r) => setItems(r.data?.results || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [propertyId]);

  const scroll = (dir) => {
    if (!trackRef.current) return;
    const w = trackRef.current.clientWidth;
    trackRef.current.scrollBy({ left: dir * w * 0.8, behavior: "smooth" });
  };

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <div className="bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid="similar-properties">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading font-bold text-lg text-[#09090B]">Similar Properties</h3>
          <p className="text-[11px] text-[#71717A]">Same area · price band · listing intent</p>
        </div>
        <div className="hidden sm:flex gap-2">
          <button
            onClick={() => scroll(-1)}
            className="w-9 h-9 rounded-full border border-[#E4E4E7] hover:border-[#0055FF] hover:text-[#0055FF] flex items-center justify-center transition-all"
            data-testid="similar-prev"
            aria-label="Scroll left"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => scroll(1)}
            className="w-9 h-9 rounded-full border border-[#E4E4E7] hover:border-[#0055FF] hover:text-[#0055FF] flex items-center justify-center transition-all"
            data-testid="similar-next"
            aria-label="Scroll right"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div
        ref={trackRef}
        className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory"
        style={{ scrollbarWidth: "thin" }}
      >
        {items.map((p) => (
          <Link
            key={p.id}
            to={`/property/${p.id}`}
            className="snap-start shrink-0 w-[240px] bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm overflow-hidden hover:border-[#0055FF] hover:shadow-md transition-all"
            data-testid={`similar-card-${p.id}`}
          >
            <div className="aspect-[4/3] bg-[#F4F4F5] relative overflow-hidden">
              {p.images?.[0] ? (
                <>
                  <img
                    aria-hidden="true"
                    src={resolveImage(p.images[0], { w: 240 })}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-50 pointer-events-none"
                  />
                  <img loading="lazy" src={resolveImage(p.images[0], { w: 400 })} alt={p.title} className="relative w-full h-full object-cover" />
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#A1A1AA] bg-[#F4F4F5]">
                  <Home size={32} strokeWidth={1} />
                </div>
              )}
              {p.listing_intent && p.listing_intent !== "sale" && (
                <span className="absolute top-2 left-2 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-[#0055FF] text-white">
                  For {p.listing_intent}
                </span>
              )}
            </div>
            <div className="p-2.5">
              <div className="font-semibold text-[12.5px] text-[#09090B] truncate">{p.title}</div>
              <div className="text-[10.5px] text-[#52525B] truncate flex items-center gap-1 mt-0.5">
                <MapPin size={9} /> {p.main_area || p.area || "—"} · {p.bhk}
              </div>
              <div className="font-mono-data font-bold text-[13px] text-[#0055FF] mt-1">
                {formatINR(p.price)}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
